import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Sparkles, Train, Award } from "lucide-react";
import { BUSINESS, IMAGES } from "@/config";
import { PRESS, BEFORE_AFTER, FAQS } from "@/data/site";
import SEO from "@/components/SEO";
import { track, directionsUrl } from "@/lib/analytics";
import { Reveal, MaskedLines, FadeUp } from "@/components/Reveal";
import { BookButton, CallButton } from "@/components/CTAButtons";
import ServiceCards from "@/components/ServiceCards";
import EditorialMarquee from "@/components/Marquee";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import FAQAccordion from "@/components/FAQAccordion";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { CTABanner } from "@/components/sections";

const CHAPTERS = [
  { n: "01", t: "Clinical Clean", d: "Autoclave-sterilized tools, single-use files, air-purified rooms. Hygiene you can feel." },
  { n: "02", t: "Expert Hands", d: "Color directors, licensed estheticians, and master nail artists — no rotating chairs." },
  { n: "03", t: "Honest Menu", d: "Transparent pricing and timings. No surprise upsells, ever." },
];

const TRUST = [
  { icon: ShieldCheck, label: "Hospital-grade sanitation" },
  { icon: Award, label: "Best of Manhattan 2024" },
  { icon: Sparkles, label: "Clean & non-toxic lines" },
  { icon: Train, label: "3 min from A/C/E" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const nb = useRef(null);
  const { scrollYProgress: nbP } = useScroll({ target: nb, offset: ["start end", "end start"] });
  const skyY = useTransform(nbP, [0, 1], ["-8%", "8%"]);

  return (
    <>
      <SEO
        title="Luxury Hair, Nails, Facials & Waxing in Hell's Kitchen, NYC"
        description="Maison Neuf is a full-service luxury salon in Hell's Kitchen, Manhattan. Expert balayage & color, gel manicures, custom facials & HydraFacial, waxing, brows & lashes. Book online in NYC."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] w-full overflow-hidden" data-testid="home-hero">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={IMAGES.heroInterior} alt="Dimly lit luxury salon interior in Hell's Kitchen, New York" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/20" />
        </motion.div>

        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-28 md:px-10 md:pb-24">
          <FadeUp>
            <p className="label mb-6 flex items-center gap-3 text-foreground/80">
              <span className="inline-block h-px w-10 bg-wine" /> {BUSINESS.neighborhood}
            </p>
          </FadeUp>
          <h1 className="font-display text-[3rem] leading-[0.95] tracking-tight sm:text-7xl lg:text-[8rem]">
            <MaskedLines lines={["Hair. Nails.", "Skin. After dark."]} />
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
              A full-service luxury salon in Hell's Kitchen — color, nails, facials, and waxing by New York's most requested specialists.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <BookButton testid="hero-book-button" />
              <CallButton label={`Call / Text · ${BUSINESS.phoneDisplay}`} testid="hero-call-button" />
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-6 right-5 z-10 hidden md:block md:right-10">
          <span className="label">Scroll to explore ↓</span>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-border bg-background" data-testid="trust-strip">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-px bg-border md:grid-cols-4">
          {TRUST.map((t, i) => (
            <div key={i} className="flex items-center gap-3 bg-background px-5 py-6 md:px-8">
              <t.icon className="h-5 w-5 shrink-0 text-wine" strokeWidth={1.5} />
              <span className="font-mono text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO / MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32" data-testid="manifesto">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="label mb-8 text-wine">The Maison Neuf standard</p>
              <h2 className="font-display text-4xl leading-[1.05] md:text-6xl">
                One address for every kind of self-care in Midtown West.
              </h2>
              <p className="mt-8 max-w-md text-muted-foreground">
                Between meetings, before the curtain, after a red-eye — New Yorkers don't have time to bounce between studios. So we built one elevated space for hair, nails, skin, and waxing, staffed by specialists who take their craft seriously and your time even more so.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:pt-4">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="flex gap-6 border-t border-border py-8">
                  <span className="font-mono text-sm text-wine">{c.n}</span>
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl">{c.t}</h3>
                    <p className="mt-3 text-muted-foreground">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE CATEGORY CARDS */}
      <section className="border-t border-border" data-testid="home-services">
        <div className="mx-auto max-w-[1400px] px-5 pt-20 md:px-10 md:pt-28">
          <Reveal>
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <h2 className="max-w-2xl font-display text-4xl leading-[1.05] md:text-6xl">Four studios. One elevated house.</h2>
              <Link to="/services" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground link-underline">
                View full price menu <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="mx-auto mt-14 max-w-[1400px] px-5 md:px-10">
          <ServiceCards />
        </div>
      </section>

      <div className="mt-24 md:mt-32">
        <EditorialMarquee />
      </div>

      {/* LOOKBOOK / BEFORE-AFTER PREVIEW */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32" data-testid="home-lookbook">
        <Reveal>
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="label mb-6 text-wine">The Lookbook</p>
              <h2 className="max-w-2xl font-display text-4xl leading-[1.05] md:text-6xl">Proof, dragged into focus.</h2>
              <p className="mt-6 max-w-md text-muted-foreground">Slide to see real transformations. Then book the look.</p>
            </div>
            <Link to="/gallery" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground link-underline">
              Explore full gallery <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-2">
          {BEFORE_AFTER.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.1}>
              <BeforeAfterSlider before={b.before} after={b.after} title={b.title} alt={b.title} />
              <div className="mt-4">
                <BookButton label="Book this look" category={b.cat} solid={false} testid={`book-look-${b.id}`} className="!py-3" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NEIGHBORHOOD / TRANSIT */}
      <section ref={nb} className="relative overflow-hidden border-y border-border" data-testid="home-neighborhood">
        <motion.div style={{ y: skyY }} className="absolute inset-0 h-[120%]">
          <img src={IMAGES.skyline} alt="Manhattan skyline at night near Hell's Kitchen" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-background/40" />
        </motion.div>
        <div className="relative z-10 mx-auto grid max-w-[1400px] gap-12 px-5 py-24 md:grid-cols-2 md:px-10 md:py-32">
          <Reveal>
            <p className="label mb-6 text-wine">The neighborhood</p>
            <h2 className="font-display text-4xl leading-[1.05] md:text-6xl">Right where the city moves.</h2>
            <p className="mt-6 max-w-md text-muted-foreground">
              {BUSINESS.addressLine1}, {BUSINESS.addressLine2}. {BUSINESS.crossStreets} — minutes from Port Authority, the Theater District, and Times Square.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="space-y-5">
              {BUSINESS.transit.map((t, i) => (
                <li key={i} className="flex items-start gap-4 border-t border-border pt-5">
                  <Train className="mt-0.5 h-5 w-5 shrink-0 text-wine" strokeWidth={1.5} />
                  <span className="text-foreground/90">{t}</span>
                </li>
              ))}
            </ul>
            <a href={directionsUrl(BUSINESS)} target="_blank" rel="noopener noreferrer" onClick={() => track("directions_click", { location: "home_neighborhood" })} className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-foreground hover:text-foreground link-underline">
              Get directions <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* REVIEWS + PRESS */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32" data-testid="home-reviews">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="label mb-8 text-wine">What New York says</p>
            <ReviewsCarousel />
          </div>
          <div className="lg:col-span-5 lg:border-l lg:border-border lg:pl-12">
            <p className="label mb-8">As seen in</p>
            <div className="grid grid-cols-2 gap-y-8">
              {PRESS.map((p) => (
                <span key={p} className="font-display text-xl italic text-muted-foreground">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="border-t border-border" data-testid="home-faq">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-24 md:grid-cols-12 md:px-10 md:py-32">
          <div className="md:col-span-4">
            <Reveal>
              <p className="label mb-6 text-wine">Good to know</p>
              <h2 className="font-display text-4xl leading-[1.05] md:text-5xl">Before you book.</h2>
              <Link to="/faq" className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground link-underline">
                All policies & FAQ <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <FAQAccordion items={FAQS.slice(0, 4)} />
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
